import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import TableToolbar from "../components/TableToolbar";
import DataTable from "../components/DataTable";
import Toast, { useToast } from "../components/Toast";
import {
  obtenerUsuarios,
  obtenerUsuario,
  crearUsuario,
  actualizarUsuario,
  eliminarUsuario,
} from "../services/usuarioService";

const EMPTY_FORM = {
  Clave: "",
  Nombre: "",
  Password: "",
  DiasPwd: 90,
  Status: true,
  AccesoDashboard: true,
  AccesoCotizaciones: true,
  AccesoFacturas: true,
  AccesoPagos: true,
  AccesoProveedores: true,
  AccesoReportes: true,
  AccesoUsuarios: false,
  AccesoClientes: true,
  AccesoProductos: true,
  VerTodasCotizaciones: false,
};

const MODULOS = [
  { key: "AccesoDashboard",    label: "Dashboard"    },
  { key: "AccesoCotizaciones", label: "Cotizaciones" },
  { key: "AccesoFacturas",     label: "Facturas"     },
  { key: "AccesoPagos",        label: "Pagos"        },
  { key: "AccesoProveedores",  label: "Proveedores"  },
  { key: "AccesoReportes",     label: "Reportes"     },
  { key: "AccesoUsuarios",     label: "Usuarios"     },
  { key: "AccesoClientes",     label: "Clientes"     },
  { key: "AccesoProductos",    label: "Productos"    },
];

const PERMISOS_ESPECIALES = [
  { key: "VerTodasCotizaciones", label: "Ver todas las cotizaciones" },
];

const CheckIcon = ({ ok }) =>
  ok ? (
    <span style={{ color: "var(--clr-success)", fontWeight: 700, fontSize: 15 }}>✓</span>
  ) : (
    <span style={{ color: "#cbd5e1", fontSize: 15 }}>—</span>
  );

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

export default function UsuariosPage() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [savedPassword, setSavedPassword] = useState("");
  const [savedFecha, setSavedFecha] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { toasts, closeToast, error: toastError, success: toastSuccess } = useToast();

  const cargar = useCallback(() => {
    const empresaId = Number(localStorage.getItem("empresaId") || 0);
    setLoading(true);
    obtenerUsuarios(empresaId)
      .then(({ data }) => {
        const lista = Array.isArray(data) ? data : (data?.data ?? data?.Data ?? []);
        setRows(lista.map((u) => ({
          id:                  u.Id    ?? u.id,
          clave:               u.Clave ?? u.clave ?? "",
          nombre:              u.Nombre ?? u.nombre ?? "",
          diasPwd:             u.DiasPwd ?? u.diasPwd ?? 0,
          status:              u.Status ?? u.status ?? false,
          accesoDashboard:     u.AccesoDashboard    ?? u.accesoDashboard    ?? true,
          accesoCotizaciones:  u.AccesoCotizaciones ?? u.accesoCotizaciones ?? true,
          accesoFacturas:      u.AccesoFacturas     ?? u.accesoFacturas     ?? true,
          accesoPagos:         u.AccesoPagos        ?? u.accesoPagos        ?? true,
          accesoProveedores:   u.AccesoProveedores  ?? u.accesoProveedores  ?? true,
          accesoReportes:         u.AccesoReportes          ?? u.accesoReportes          ?? true,
          accesoUsuarios:         u.AccesoUsuarios          ?? u.accesoUsuarios          ?? false,
          accesoClientes:         u.AccesoClientes          ?? u.accesoClientes          ?? true,
          accesoProductos:        u.AccesoProductos         ?? u.accesoProductos         ?? true,
          verTodasCotizaciones:   u.VerTodasCotizaciones    ?? u.verTodasCotizaciones    ?? false,
        })));
      })
      .catch((e) => toastError(e?.response?.data?.title || e?.message || "No se pudieron cargar los usuarios"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.clave.toLowerCase().includes(q) || r.nombre.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setSavedPassword("");
    setSavedFecha("");
    setShowModal(true);
  };

  const openEdit = async (row) => {
    try {
      const { data: u } = await obtenerUsuario(row.id);
      const pwd = u.Password ?? u.password ?? "";
      setSavedPassword(pwd);
      setSavedFecha(u.Fecha_Cambio_Pwd ?? u.fecha_Cambio_Pwd ?? new Date().toISOString());
      setForm({
        Id:                  u.Id    ?? u.id,
        Clave:               u.Clave ?? u.clave ?? "",
        Nombre:              u.Nombre ?? u.nombre ?? "",
        Password:            pwd,
        DiasPwd:             u.DiasPwd ?? u.diasPwd ?? 90,
        Status:              u.Status ?? u.status ?? true,
        AccesoDashboard:     u.AccesoDashboard    ?? u.accesoDashboard    ?? true,
        AccesoCotizaciones:  u.AccesoCotizaciones ?? u.accesoCotizaciones ?? true,
        AccesoFacturas:      u.AccesoFacturas     ?? u.accesoFacturas     ?? true,
        AccesoPagos:         u.AccesoPagos        ?? u.accesoPagos        ?? true,
        AccesoProveedores:   u.AccesoProveedores  ?? u.accesoProveedores  ?? true,
        AccesoReportes:         u.AccesoReportes          ?? u.accesoReportes          ?? true,
        AccesoUsuarios:         u.AccesoUsuarios          ?? u.accesoUsuarios          ?? false,
        AccesoClientes:         u.AccesoClientes          ?? u.accesoClientes          ?? true,
        AccesoProductos:        u.AccesoProductos         ?? u.accesoProductos         ?? true,
        VerTodasCotizaciones:   u.VerTodasCotizaciones    ?? u.verTodasCotizaciones    ?? false,
      });
      setShowModal(true);
    } catch {
      toastError("No fue posible cargar el usuario.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const isEdit = !!form.Id;
      const payload = {
        ...form,
        Password: form.Password || savedPassword,
        Fecha_Cambio_Pwd: savedFecha || new Date().toISOString(),
        ...(!isEdit && { EmpresaId: Number(localStorage.getItem("empresaId") || 0) }),
      };
      if (isEdit) {
        await actualizarUsuario(form.Id, payload);
        toastSuccess("Usuario actualizado correctamente.");
      } else {
        await crearUsuario(payload);
        toastSuccess("Usuario creado correctamente.");
      }
      setShowModal(false);
      cargar();
    } catch (err) {
      const d = err?.response?.data;
      if (d?.errors && typeof d.errors === "object") {
        toastError(Object.entries(d.errors).map(([k, arr]) => `${k}: ${(arr ?? []).join(" ")}`).join(" | "));
      } else {
        toastError(d?.title || d?.message || err?.message || "No se pudo guardar.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await eliminarUsuario(confirmDelete.id);
      setConfirmDelete(null);
      toastSuccess("Usuario eliminado.");
      cargar();
    } catch (err) {
      toastError(err?.response?.data?.title || err?.message || "No se pudo eliminar.");
    }
  };

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setBool  = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.checked }));

  const columns = [
    { key: "clave",  title: "Clave",  style: { width: 100 } },
    { key: "nombre", title: "Nombre" },
    { key: "diasPwd", title: "Días Pwd", style: { width: 85, textAlign: "center" } },
    {
      key: "status", title: "Estatus", style: { width: 90 },
      render: (r) => (
        <span className={`lmd-badge ${r.status ? "lmd-badge-success" : "lmd-badge-neutral"}`}>
          {r.status ? "Activo" : "Inactivo"}
        </span>
      ),
    },
    { key: "accesoDashboard",    title: "Dash.",  style: { width: 60, textAlign: "center" }, render: (r) => <CheckIcon ok={r.accesoDashboard}    /> },
    { key: "accesoCotizaciones", title: "Cotiz.", style: { width: 65, textAlign: "center" }, render: (r) => <CheckIcon ok={r.accesoCotizaciones} /> },
    { key: "accesoFacturas",     title: "Fact.",  style: { width: 60, textAlign: "center" }, render: (r) => <CheckIcon ok={r.accesoFacturas}     /> },
    { key: "accesoPagos",        title: "Pagos",  style: { width: 65, textAlign: "center" }, render: (r) => <CheckIcon ok={r.accesoPagos}        /> },
    { key: "accesoProveedores",  title: "Prov.",  style: { width: 60, textAlign: "center" }, render: (r) => <CheckIcon ok={r.accesoProveedores}  /> },
    { key: "accesoReportes",     title: "Rep.",   style: { width: 55, textAlign: "center" }, render: (r) => <CheckIcon ok={r.accesoReportes}     /> },
    { key: "accesoUsuarios",     title: "Usrs.",  style: { width: 60, textAlign: "center" }, render: (r) => <CheckIcon ok={r.accesoUsuarios}     /> },
    { key: "accesoClientes",     title: "Cltes.", style: { width: 60, textAlign: "center" }, render: (r) => <CheckIcon ok={r.accesoClientes}     /> },
    { key: "accesoProductos",    title: "Prods.", style: { width: 60, textAlign: "center" }, render: (r) => <CheckIcon ok={r.accesoProductos}    /> },
    {
      key: "acciones", title: "", style: { width: 150 },
      render: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="lmd-btn lmd-btn-warning lmd-btn-sm" onClick={() => openEdit(r)}>Editar</button>
          <button className="lmd-btn lmd-btn-danger  lmd-btn-sm" onClick={() => setConfirmDelete(r)}>Eliminar</button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader title="Usuarios" subtitle="Mantenimiento de usuarios del sistema" />

      <div className="lmd-card">
        <TableToolbar
          searchPlaceholder="Buscar por clave o nombre..."
          searchValue={search}
          onSearchChange={setSearch}
          onSearch={() => {}}
          onAdd={openNew}
          addText="Nuevo Usuario"
        />

        {loading && (
          <div className="lmd-loading-bar">
            <div className="lmd-spinner" /> Cargando usuarios...
          </div>
        )}

        <DataTable columns={columns} rows={filtered} keyField="id" />

        <div className="lmd-pagination">
          <span className="lmd-pagination-info">
            Total: <strong>{filtered.length}</strong> usuarios
          </span>
        </div>
      </div>

      {/* ── Modal crear / editar ── */}
      {showModal && (
        <div className="lmd-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="lmd-modal lmd-modal-md" onClick={(e) => e.stopPropagation()}>
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">{form.Id ? "Editar Usuario" : "Nuevo Usuario"}</h3>
              <button className="lmd-modal-close" onClick={() => setShowModal(false)}><CloseIcon /></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="lmd-modal-body">
                <div className="lmd-form-grid lmd-form-grid-2" style={{ gap: 16 }}>

                  <div>
                    <label className="lmd-label">Clave *</label>
                    <input className="lmd-input" maxLength={8} required value={form.Clave} onChange={setField("Clave")} placeholder="Max. 8 caracteres" />
                  </div>

                  <div>
                    <label className="lmd-label">Nombre *</label>
                    <input className="lmd-input" maxLength={40} required value={form.Nombre} onChange={setField("Nombre")} placeholder="Nombre completo" />
                  </div>

                  <div>
                    <label className="lmd-label">Contraseña{form.Id ? " (dejar en blanco para no cambiar)" : " *"}</label>
                    <input
                      type="password"
                      className="lmd-input"
                      required={!form.Id}
                      value={form.Password}
                      onChange={setField("Password")}
                      autoComplete="new-password"
                    />
                  </div>

                  <div>
                    <label className="lmd-label">Días de validez de contraseña</label>
                    <input
                      type="number" className="lmd-input" min={0} max={9999}
                      value={form.DiasPwd}
                      onChange={(e) => setForm((f) => ({ ...f, DiasPwd: parseInt(e.target.value, 10) || 0 }))}
                    />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 20 }}>
                    <input type="checkbox" id="usr-status" checked={form.Status} onChange={setBool("Status")}
                      style={{ width: 17, height: 17, cursor: "pointer", accentColor: "var(--clr-primary)" }} />
                    <label htmlFor="usr-status" className="lmd-label" style={{ marginBottom: 0, cursor: "pointer" }}>
                      Usuario activo
                    </label>
                  </div>

                  {/* Acceso a módulos */}
                  <div style={{ gridColumn: "1/-1", marginTop: 8, paddingTop: 12, borderTop: "1px solid var(--clr-border)" }}>
                    <label className="lmd-label" style={{ marginBottom: 12, display: "block" }}>Acceso a módulos</label>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px 24px" }}>
                      {MODULOS.map(({ key, label }) => (
                        <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <input
                            type="checkbox" id={`acc-${key}`}
                            checked={form[key] ?? true}
                            onChange={setBool(key)}
                            style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--clr-primary)" }}
                          />
                          <label htmlFor={`acc-${key}`} className="lmd-label" style={{ marginBottom: 0, cursor: "pointer" }}>
                            {label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Permisos especiales */}
                  <div style={{ gridColumn: "1/-1", marginTop: 8, paddingTop: 12, borderTop: "1px solid var(--clr-border)" }}>
                    <label className="lmd-label" style={{ marginBottom: 12, display: "block" }}>Permisos especiales</label>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px 24px" }}>
                      {PERMISOS_ESPECIALES.map(({ key, label }) => (
                        <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <input
                            type="checkbox" id={`perm-${key}`}
                            checked={form[key] ?? false}
                            onChange={setBool(key)}
                            style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--clr-primary)" }}
                          />
                          <label htmlFor={`perm-${key}`} className="lmd-label" style={{ marginBottom: 0, cursor: "pointer" }}>
                            {label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>

              <div className="lmd-modal-footer">
                <button type="button" className="lmd-btn lmd-btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal confirmar eliminar ── */}
      {confirmDelete && (
        <div className="lmd-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="lmd-modal lmd-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">Confirmar eliminacion</h3>
              <button className="lmd-modal-close" onClick={() => setConfirmDelete(null)}><CloseIcon /></button>
            </div>
            <div className="lmd-modal-body">
              <p style={{ marginBottom: 8 }}>
                ¿Desea eliminar al usuario <strong>{confirmDelete.nombre}</strong> ({confirmDelete.clave})?
              </p>
              <p style={{ color: "var(--clr-danger)", fontSize: "var(--fs-sm)", margin: 0 }}>
                Esta accion no se puede deshacer.
              </p>
            </div>
            <div className="lmd-modal-footer">
              <button className="lmd-btn lmd-btn-secondary" onClick={() => setConfirmDelete(null)}>Cancelar</button>
              <button className="lmd-btn lmd-btn-danger"    onClick={handleDelete}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
